import { useState, useMemo, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
} from 'recharts';
import { FileDown, ChevronsUpDown, ChevronUp, ChevronDown, ExternalLink, Search, X, Globe } from '@/utils/icons';
import { cn } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { InfoTooltip } from '@/components/common/InfoTooltip';
import { ExportMenu } from '@/components/common/ExportMenu';
import { KpiStrip } from '@/components/common/KpiStrip';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { PageLoader } from '@/components/common/LoadingSpinner';
import { StatusDot } from '@/components/common/StatusBadge';
import { usePortfolioMetrics } from '@/hooks/useMetrics';
import { usePortfolioDetail } from '@/hooks/usePortfolios';
import { formatPercentage, formatTimeAgo } from '@/utils/formatters';
import { getUptimeColor, createPieLabel, renderActiveShape } from '@/utils/calculations';
import { STATUS, NEUTRAL } from '@/utils/dls-tokens';
import {
  createBrandedPdf,
  addMetricRow,
  addSectionHeading,
  addBrandedTable,
  downloadPdf,
  loadLogoForPdf,
  exportFilename,
} from '@/utils/pdf';
import { downloadCsv } from '@/utils/csv';
import { PIE_COLORS } from '@/utils/chart-colors';
import { getPortfolioBulletin } from '@/services/api';
import type { Period, PortfolioMetrics } from '@/types/metrics';

// Higher threshold for charts with many segments — small slices still visible via tooltip
const pieLabel = createPieLabel(0.08);

type SortKey =
  | 'portfolio'
  | 'totalNodes'
  | 'aliveNodes'
  | 'unreachableNodes'
  | 'uptimePercent'
  | 'longestOfflineDays';

function sortPortfolios(
  list: PortfolioMetrics[],
  sortBy: SortKey,
  sortOrder: 'asc' | 'desc'
): PortfolioMetrics[] {
  return [...list].sort((a, b) => {
    let cmp = 0;
    if (sortBy === 'portfolio') {
      cmp = (a.portfolio || '').localeCompare(b.portfolio || '');
    } else {
      cmp = (a[sortBy] ?? 0) - (b[sortBy] ?? 0);
    }
    return sortOrder === 'asc' ? cmp : -cmp;
  });
}

export function PortfolioView() {
  const { portfolios, loading: portfoliosLoading } = usePortfolioMetrics();
  const [selectedPortfolio, setSelectedPortfolio] = useState<string>('');
  const [period] = useState<Period>('24h');
  const [bulletinLoading, setBulletinLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [activeDistIndex, setActiveDistIndex] = useState<number | undefined>(undefined);
  const [sortBy, setSortBy] = useState<SortKey>('portfolio');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const detailRef = useRef<HTMLDivElement>(null);

  // Scroll to portfolio detail section when a portfolio is selected
  useEffect(() => {
    if (selectedPortfolio && detailRef.current) {
      // Temporarily ensure enough scroll room for first expansion
      detailRef.current.style.minHeight = '100vh';
      requestAnimationFrame(() => {
        detailRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        // Remove temporary height after scroll animation completes
        setTimeout(() => {
          if (detailRef.current) detailRef.current.style.minHeight = '';
        }, 700);
      });
    }
  }, [selectedPortfolio]);

  const apiPortfolio = selectedPortfolio === '__blank__' ? '' : selectedPortfolio;
  const displayPortfolio = selectedPortfolio === '__blank__' ? 'Unassigned' : selectedPortfolio;
  const { data: portfolioDetail, loading: detailLoading } = usePortfolioDetail(
    apiPortfolio,
    period
  );

  // Filtered + sorted portfolios
  const filteredPortfolios = useMemo(() => {
    const filtered = search
      ? portfolios.filter((p) =>
          (p.portfolio || 'Unassigned').toLowerCase().includes(search.toLowerCase())
        )
      : portfolios;
    return sortPortfolios(filtered, sortBy, sortOrder);
  }, [portfolios, search, sortBy, sortOrder]);

  // Aggregate stats
  const totalNodes = portfolios.reduce((s, r) => s + r.totalNodes, 0);
  const totalOnline = portfolios.reduce((s, r) => s + r.aliveNodes, 0);
  const totalOffline = totalNodes - totalOnline;
  const avgUptime =
    totalNodes > 0
      ? portfolios.reduce((s, r) => s + r.uptimePercent * r.totalNodes, 0) / totalNodes
      : 100;

  // Chart data
  const uptimeChartData = useMemo(
    () =>
      [...portfolios]
        .sort((a, b) => a.uptimePercent - b.uptimePercent)
        .map((p) => ({
          portfolio: p.portfolio || 'Unassigned',
          uptime: Number(p.uptimePercent.toFixed(1)),
          portfolioKey: p.portfolio || '__blank__',
        })),
    [portfolios]
  );

  const distributionData = useMemo(
    () =>
      [...portfolios]
        .filter((p) => p.totalNodes > 0)
        .sort((a, b) => b.totalNodes - a.totalNodes)
        .map((p) => ({
          name: p.portfolio || 'Unassigned',
          value: p.totalNodes,
          portfolioKey: p.portfolio || '__blank__',
        })),
    [portfolios]
  );

  const handleSort = (column: SortKey) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder(column === 'portfolio' ? 'asc' : 'desc');
    }
  };

  // --- Export handlers ---

  const generateBulletinPdf = async (portfolio: string, portfolioLabel: string) => {
    const logoData = await loadLogoForPdf();
    const bulletin = await getPortfolioBulletin(portfolio, '30d');
    const layout = createBrandedPdf({
      title: `Portfolio ${portfolioLabel} Uptime Bulletin`,
      subtitle: `${bulletin.summary.totalNodes} Nodes — ${bulletin.summary.uptimePercent.toFixed(1)}% uptime — ${bulletin.period} period`,
      logoData,
    });

    let y = addMetricRow(
      layout.doc,
      [
        { label: 'Uptime', value: `${bulletin.summary.uptimePercent.toFixed(1)}%` },
        { label: 'Nodes', value: String(bulletin.summary.totalNodes) },
        { label: 'Online', value: String(bulletin.summary.aliveNodes) },
        { label: 'Incidents', value: String(bulletin.summary.incidents) },
      ],
      layout.startY,
      layout
    );

    if (bulletin.summary.mttr > 0) {
      const mttrMin = Math.round(bulletin.summary.mttr / 60);
      y = addSectionHeading(layout.doc, `Mean Time to Recovery: ${mttrMin} min`, y, layout);
    }

    y = addSectionHeading(layout.doc, 'Node Status', y, layout);
    y = addBrandedTable(
      layout.doc,
      ['Name', 'Status', 'Profile', 'Version', 'Last Contact'],
      bulletin.nodes.map((n) => [
        n.name,
        n.currentNodeState,
        n.profileName,
        n.softwareVersion,
        n.lastContactedAt ? new Date(n.lastContactedAt).toLocaleString() : '',
      ]),
      y,
      layout
    );

    if (bulletin.incidents.length > 0) {
      y = addSectionHeading(
        layout.doc,
        `Incident Timeline (${bulletin.incidents.length} events)`,
        y + 6,
        layout
      );
      y = addBrandedTable(
        layout.doc,
        ['Node', 'From', 'To', 'Time', 'Duration'],
        bulletin.incidents.map((e) => [
          e.nodeName,
          e.previousState ?? '',
          e.newState,
          new Date(e.changedAt).toLocaleString(),
          e.durationSeconds ? `${Math.round(e.durationSeconds / 60)}m` : 'ongoing',
        ]),
        y,
        layout
      );
    }

    if (bulletin.services.length > 0) {
      y = addSectionHeading(
        layout.doc,
        `Service Health (${bulletin.services.length})`,
        y + 6,
        layout
      );
      y = addBrandedTable(
        layout.doc,
        ['Service', 'Node', 'Status', 'Containers', 'Restarts'],
        bulletin.services.map((s) => [
          s.name,
          s.nodeName,
          s.healthStatus ?? 'Unknown',
          s.runningContainers ?? '',
          s.totalRestartCount,
        ]),
        y,
        layout
      );
    }

    if (bulletin.cloudConnectors.length > 0) {
      y = addSectionHeading(layout.doc, 'Cloud Connectors', y + 6, layout);
      addBrandedTable(
        layout.doc,
        ['Name', 'Status'],
        bulletin.cloudConnectors.map((cc) => [cc.name, cc.currentNodeState]),
        y,
        layout
      );
    }

    return layout;
  };

  const handleBulletinExport = async (portfolio?: string) => {
    setBulletinLoading(true);
    try {
      if (portfolio !== undefined) {
        // Single portfolio bulletin
        const label = portfolio === '' ? 'Unassigned' : portfolio;
        const layout = await generateBulletinPdf(portfolio, label);
        downloadPdf(layout.doc, exportFilename(`portfolio-bulletin-${label}`, 'pdf'));
      } else {
        // All portfolios combined bulletin
        const logoData = await loadLogoForPdf();
        const layout = createBrandedPdf({
          title: 'All Portfolios Uptime Bulletin',
          subtitle: `${portfolios.length} portfolios — ${totalNodes} Nodes — 30d period`,
          logoData,
        });

        let y = addMetricRow(
          layout.doc,
          [
            { label: 'Portfolios', value: String(portfolios.length) },
            { label: 'Total Nodes', value: String(totalNodes) },
            { label: 'Online', value: String(totalOnline) },
            { label: 'Offline', value: String(totalOffline) },
          ],
          layout.startY,
          layout
        );

        for (const p of portfolios) {
          const pName = p.portfolio || 'Unassigned';
          const apiP = p.portfolio || '';
          try {
            const bulletin = await getPortfolioBulletin(apiP, '30d');

            y = addSectionHeading(layout.doc, pName, y + 6, layout);
            y = addMetricRow(
              layout.doc,
              [
                { label: 'Uptime', value: `${bulletin.summary.uptimePercent.toFixed(1)}%` },
                { label: 'Nodes', value: String(bulletin.summary.totalNodes) },
                { label: 'Online', value: String(bulletin.summary.aliveNodes) },
                { label: 'Incidents', value: String(bulletin.summary.incidents) },
              ],
              y,
              layout
            );

            y = addBrandedTable(
              layout.doc,
              ['Name', 'Status', 'Profile', 'Version'],
              bulletin.nodes.map((n) => [
                n.name,
                n.currentNodeState,
                n.profileName,
                n.softwareVersion,
              ]),
              y,
              layout
            );
          } catch {
            y = addSectionHeading(layout.doc, `${pName} — failed to load`, y + 6, layout);
          }
        }

        downloadPdf(layout.doc, exportFilename('all-portfolios-bulletin', 'pdf'));
      }
    } catch (err) {
      console.error('Failed to generate bulletin:', err);
    } finally {
      setBulletinLoading(false);
    }
  };

  const handleExportPdf = async () => {
    if (!portfolios.length) return;

    const logoData = await loadLogoForPdf();
    const layout = createBrandedPdf({
      title: 'Portfolio Uptime Report',
      subtitle: `${portfolios.length} portfolios`,
      logoData,
    });

    let y = addMetricRow(
      layout.doc,
      [
        { label: 'Portfolios', value: String(portfolios.length) },
        { label: 'Total Nodes', value: String(totalNodes) },
        { label: 'Online', value: String(totalOnline) },
        { label: 'Offline', value: String(totalOffline) },
      ],
      layout.startY,
      layout
    );

    y = addSectionHeading(layout.doc, 'All Portfolios', y, layout);
    addBrandedTable(
      layout.doc,
      ['Portfolio', 'Total Nodes', 'Online', 'Offline', 'Uptime %'],
      portfolios.map((r) => [
        r.portfolio || 'Unassigned',
        r.totalNodes,
        r.aliveNodes,
        r.unreachableNodes,
        `${r.uptimePercent.toFixed(2)}%`,
      ]),
      y,
      layout
    );

    downloadPdf(layout.doc, exportFilename('portfolio-report', 'pdf'));
  };

  const handleExportCsv = () => {
    if (!portfolios.length) return;

    downloadCsv(
      portfolios.map((r) => ({
        Portfolio: r.portfolio || 'Unassigned',
        'Total Nodes': r.totalNodes,
        Online: r.aliveNodes,
        Offline: r.unreachableNodes,
        'Uptime %': r.uptimePercent.toFixed(2),
        'Longest Offline (days)': r.longestOfflineDays.toFixed(1),
        'Longest Offline Node': r.longestOfflineNodeName || '',
      })),
      exportFilename('portfolio-report', 'csv'),
      { report: 'Portfolios' }
    );
  };

  if (portfoliosLoading) {
    return <PageLoader />;
  }

  const sortableColumns: { key: SortKey; label: string; align?: string }[] = [
    { key: 'portfolio', label: 'Portfolio' },
    { key: 'totalNodes', label: 'Total Nodes', align: 'dls-text-right' },
    { key: 'aliveNodes', label: 'Online', align: 'dls-text-right' },
    { key: 'unreachableNodes', label: 'Offline', align: 'dls-text-right' },
    { key: 'uptimePercent', label: 'Uptime', align: 'dls-text-right' },
    { key: 'longestOfflineDays', label: 'Longest Offline', align: 'dls-text-right' },
  ];

  return (
    <div className="dls-space-y-6">
      {/* Header */}
      <div className="dls-flex dls-items-center dls-justify-between dls-flex-wrap dls-gap-4">
        <div>
          <h1 className="dls-page-title dls-flex dls-items-center dls-gap-2"><Globe className="dls-h-7 dls-w-7 dls-text-neeve-primary" />Portfolios</h1>
          <p className="dls-text-muted-foreground">Uptime metrics and node distribution by portfolio</p>
        </div>
        <ExportMenu
          onExportPdf={handleExportPdf}
          onExportCsv={handleExportCsv}
          label="Export Report"
        />
      </div>

      {/* Summary stat strip */}
      <KpiStrip
        items={[
          { label: 'Avg Uptime', value: formatPercentage(avgUptime), tooltip: 'Node-weighted average uptime across all portfolios.', valueClassName: getUptimeColor(avgUptime) },
          { label: 'Total Nodes', value: totalNodes, tooltip: 'Total Nodes across all portfolios.' },
          { label: 'Online', value: totalOnline, tooltip: 'Nodes currently reporting ALIVE state across all portfolios.', variant: 'success' },
          { label: 'Offline', value: totalOffline, tooltip: 'Nodes currently in UNREACHABLE state across all portfolios.', variant: totalOffline > 0 ? 'danger' : 'default' },
          { label: 'Portfolios', value: portfolios.length, tooltip: 'Total number of portfolios inferred from metadata labels, CC connectivity, Node name prefixes, and location data.' },
        ]}
      />

      {/* Charts */}
      <div className="dls-grid dls-gap-6 lg:dls-grid-cols-2">
        {/* Uptime by Portfolio */}
        <Card>
          <CardHeader>
            <CardTitle className="dls-flex dls-items-center dls-gap-1.5">
              Uptime by Portfolio
              <InfoTooltip text="Current uptime percentage per portfolio, sorted lowest first to highlight portfolios needing attention." />
            </CardTitle>
          </CardHeader>
          <CardContent>
            {uptimeChartData.length === 0 ? (
              <p className="dls-text-sm dls-text-muted-foreground dls-py-8 dls-text-center">
                No portfolio data available.
              </p>
            ) : (
              <div className="dls-h-[280px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={uptimeChartData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                    <XAxis type="number" domain={[0, 100]} unit="%" />
                    <YAxis
                      dataKey="portfolio"
                      type="category"
                      width={120}
                      fontSize={12}
                      tickFormatter={(v) => (v.length > 16 ? `${v.slice(0, 14)}...` : v)}
                    />
                    <Tooltip
                      formatter={(value: number) => [`${value}%`, 'Uptime']}
                      labelFormatter={(label) => label}
                      cursor={{ fill: NEUTRAL.quarternary }}
                    />
                    <Bar
                      dataKey="uptime"
                      radius={[0, 4, 4, 0]}
                      barSize={20}
                      onClick={(data) => {
                        if (data?.portfolioKey) setSelectedPortfolio(data.portfolioKey as string);
                      }}
                      className="dls-cursor-pointer"
                    >
                      {uptimeChartData.map((entry, index) => (
                        <Cell
                          key={`bar-${index}`}
                          fill={
                            entry.uptime >= 99
                              ? STATUS.success.primary
                              : entry.uptime >= 95
                                ? STATUS.warning.primary
                                : STATUS.danger.primary
                          }
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Node Distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="dls-flex dls-items-center dls-gap-1.5">
              Node Distribution
              <InfoTooltip text="How Nodes are distributed across portfolios by count." />
            </CardTitle>
          </CardHeader>
          <CardContent>
            {distributionData.length === 0 ? (
              <p className="dls-text-sm dls-text-muted-foreground dls-py-8 dls-text-center">
                No portfolio data available.
              </p>
            ) : (
              <div className="dls-h-[280px] dls-flex dls-items-center">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={distributionData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={90}
                      paddingAngle={2}
                      label={pieLabel}
                      labelLine={false}
                      activeIndex={activeDistIndex}
                      activeShape={renderActiveShape}
                      onMouseEnter={(_, index) => setActiveDistIndex(index)}
                      onMouseLeave={() => setActiveDistIndex(undefined)}
                      onClick={(data) => {
                        if (data?.portfolioKey) setSelectedPortfolio(data.portfolioKey as string);
                      }}
                      className="dls-cursor-pointer"
                    >
                      {distributionData.map((_, index) => (
                        <Cell key={`pie-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value: number) => [`${value} Nodes`, 'Count']} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Portfolio comparison table */}
      <Card>
        <CardHeader>
          <div className="dls-flex dls-items-center dls-justify-between dls-flex-wrap dls-gap-4">
            <CardTitle className="dls-flex dls-items-center dls-gap-1.5">
              All Portfolios
              <InfoTooltip text="Click a row to see portfolio details and export its bulletin." />
            </CardTitle>
            <div className="dls-flex dls-items-center dls-gap-2">
              <div className="dls-relative">
                <Search className="dls-absolute dls-left-3 dls-top-1/2 -dls-translate-y-1/2 dls-h-4 dls-w-4 dls-text-muted-foreground" />
                <Input
                  placeholder="Search portfolios..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="dls-pl-9 dls-w-full sm:dls-w-64 dls-rounded-xl"
                />
              </div>
              {search && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="dls-h-6 dls-text-xs"
                  onClick={() => setSearch('')}
                >
                  <X className="dls-h-3 dls-w-3 dls-mr-1" />
                  Clear
                </Button>
              )}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="secondary" size="sm" disabled={bulletinLoading}>
                    <FileDown className="dls-h-4 dls-w-4 dls-mr-2" />
                    {bulletinLoading ? 'Generating...' : 'Export Bulletin'}
                    <ChevronDown className="dls-h-3 dls-w-3 dls-ml-1" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => handleBulletinExport()}>
                    All Portfolios Bulletin
                  </DropdownMenuItem>
                  {selectedPortfolio && (
                    <DropdownMenuItem onClick={() => handleBulletinExport(apiPortfolio)}>
                      {displayPortfolio} Bulletin
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </CardHeader>
        <CardContent className="dls-overflow-x-auto">
          <Table className="dls-min-w-[700px]">
            <TableHeader>
              <TableRow>
                {sortableColumns.map((col) => (
                  <TableHead
                    key={col.key}
                    className={cn('dls-cursor-pointer hover:dls-text-foreground dls-select-none', col.align)}
                    onClick={() => handleSort(col.key)}
                  >
                    <span className="dls-inline-flex dls-items-center dls-gap-1">
                      {col.label}
                      {sortBy === col.key ? (
                        sortOrder === 'asc' ? (
                          <ChevronUp className="dls-h-3.5 dls-w-3.5" />
                        ) : (
                          <ChevronDown className="dls-h-3.5 dls-w-3.5" />
                        )
                      ) : (
                        <ChevronsUpDown className="dls-h-3.5 dls-w-3.5 dls-text-muted-foreground/50" />
                      )}
                    </span>
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredPortfolios.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="dls-text-sm dls-text-muted-foreground dls-py-12 dls-text-center">
                    No portfolios found.
                  </TableCell>
                </TableRow>
              ) : (
                filteredPortfolios.map((p) => {
                  const key = p.portfolio || '__blank__';
                  return (
                    <TableRow
                      key={key}
                      className={cn(
                        'hover:dls-bg-muted/50 dls-transition-colors dls-cursor-pointer',
                        selectedPortfolio === key && 'dls-bg-muted'
                      )}
                      onClick={() => setSelectedPortfolio(selectedPortfolio === key ? '' : key)}
                    >
                      <TableCell className="dls-font-medium">
                        {p.portfolio || (
                          <span className="dls-text-muted-foreground dls-italic">Unassigned</span>
                        )}
                      </TableCell>
                      <TableCell className="dls-text-right">{p.totalNodes}</TableCell>
                      <TableCell className="dls-text-right dls-text-status-online">
                        {p.aliveNodes}
                      </TableCell>
                      <TableCell className="dls-text-right dls-text-status-offline">
                        {p.unreachableNodes}
                      </TableCell>
                      <TableCell className="dls-text-right dls-font-medium">
                        {formatPercentage(p.uptimePercent)}
                      </TableCell>
                      <TableCell className="dls-text-right dls-text-muted-foreground">
                        {p.longestOfflineDays > 0
                          ? `${p.longestOfflineDays.toFixed(1)}d`
                          : '\u2014'}
                        {p.longestOfflineNodeName && (
                          <span className="dls-text-xs dls-ml-1">({p.longestOfflineNodeName})</span>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Selected portfolio detail */}
      {selectedPortfolio && (
        <div ref={detailRef} className="dls-scroll-mt-20 dls-space-y-6">
          {detailLoading ? (
            <PageLoader />
          ) : portfolioDetail ? (
            <>
              <div className="dls-flex dls-items-center dls-justify-between">
                <div className="dls-flex dls-items-center dls-gap-3">
                  <Select
                    value={selectedPortfolio}
                    onValueChange={(v) => setSelectedPortfolio(v)}
                  >
                    <SelectTrigger className="dls-h-9 dls-w-auto dls-gap-2 dls-border-border dls-text-sm dls-font-semibold">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {portfolios.map((p) => {
                        const key = p.portfolio || '__blank__';
                        return (
                          <SelectItem key={key} value={key}>
                            {p.portfolio || 'Unassigned'}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => handleBulletinExport(apiPortfolio)}
                  disabled={bulletinLoading}
                >
                  <FileDown className="dls-h-4 dls-w-4 dls-mr-2" />
                  {bulletinLoading ? 'Generating...' : 'Download Bulletin'}
                </Button>
              </div>
              <KpiStrip
                items={[
                  { label: 'Portfolio Uptime', value: formatPercentage(portfolioDetail.uptimePercent), tooltip: 'Percentage of Nodes currently online in this portfolio.' },
                  { label: 'Total Nodes', value: portfolioDetail.totalNodes, tooltip: 'Number of Nodes assigned to this portfolio.' },
                  { label: 'Online', value: portfolioDetail.aliveNodes, tooltip: 'Nodes in this portfolio currently reporting ALIVE state.', variant: 'success' },
                  { label: 'Offline', value: portfolioDetail.unreachableNodes, tooltip: 'Nodes in this portfolio currently in UNREACHABLE state.', variant: portfolioDetail.unreachableNodes > 0 ? 'danger' : 'default' },
                ]}
              />

              <Card>
                <CardHeader>
                  <CardTitle className="dls-flex dls-items-center dls-gap-1.5">
                    Nodes in {displayPortfolio}
                    <InfoTooltip text="All Nodes assigned to this portfolio with current status." />
                  </CardTitle>
                </CardHeader>
                <CardContent className="dls-overflow-x-auto">
                  {portfolioDetail.nodes.length === 0 ? (
                    <p className="dls-text-sm dls-text-muted-foreground dls-py-12 dls-text-center">
                      No nodes in this portfolio.
                    </p>
                  ) : (
                    <Table className="dls-min-w-[700px]">
                      <TableHeader>
                        <TableRow>
                          <TableHead>Status</TableHead>
                          <TableHead>Name</TableHead>
                          <TableHead>S/N</TableHead>
                          <TableHead>Profile</TableHead>
                          <TableHead>Version</TableHead>
                          <TableHead>Last Contact</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {portfolioDetail.nodes.map((node) => (
                          <TableRow key={node.id}>
                            <TableCell>
                              <StatusDot state={node.currentNodeState} />
                            </TableCell>
                            <TableCell>
                              <Link
                                to={`/nodes/${node.id}`}
                                className="dls-font-medium hover:dls-underline dls-flex dls-items-center dls-gap-1"
                              >
                                {node.name}
                                <ExternalLink className="dls-h-3 dls-w-3 dls-text-muted-foreground" />
                              </Link>
                            </TableCell>
                            <TableCell className="dls-text-sm dls-text-muted-foreground dls-font-mono">
                              {node.hardwareSerialNumber || '\u2014'}
                            </TableCell>
                            <TableCell>{node.profileName}</TableCell>
                            <TableCell className="dls-font-mono dls-text-sm">
                              {node.softwareVersion || 'N/A'}
                            </TableCell>
                            <TableCell className="dls-text-sm dls-text-muted-foreground">
                              {node.lastContactedAt
                                ? formatTimeAgo(node.lastContactedAt)
                                : '\u2014'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </>
          ) : null}
        </div>
      )}
    </div>
  );
}
